<script setup lang="ts">
import type { SectionPagination } from '#shared/utils/journal-pagination'
import type { BaseJournal, JournalFootnote } from '~/utils/solr/journal'
import type { AbstractData } from '~/utils/solr/journal-utils'
import ArticleSection from '~/components/journals/ArticleSection.vue'
import { useJournalFootnotes } from '~/composables/footnotes/useJournalFootnotes'
import { useVisibleSectionSync } from '~/composables/useVisibleSectionSync'
import { handleHighlights } from '~/utils/solr/highlighting-utils'
import { buildSectionLayouts, convertJatsTitleTagsToH3 } from '~/utils/journals/section-layout'

export interface Section {
  id: string
  title?: string
  bodytext?: string
  pagination?: SectionPagination
}

const props = defineProps<{
  articleId?: string
  articleTitle?: string | null
  articleAuthors?: string[] | null
  articleFirstPage?: number | null
  articleLastPage?: number | null
  journal?: BaseJournal | null
  sections: Section[]
  footnotes?: JournalFootnote[]
  searchResult?: { id: string, highlights: string[] } | null
  abstractData?: AbstractData | null
  copyrightText?: string | null
}>()

const emit = defineEmits<{
  visibleSectionChanged: [sectionId: string | undefined]
}>()

const containers = inject(diKeys.containers, null)
const route = useRoute()
const { $parseStringToSharedDocument } = useNuxtApp()

const sectionIds = computed(() => props.sections.map(section => section.id))
const { syncVisibleSectionFromScroll } = useVisibleSectionSync({
  sectionIds,
  strategy: 'top-marker',
  topMarkerOffset: 80,
  onChange: sectionId => emit('visibleSectionChanged', sectionId),
})

const highlightScrollId = computed(() =>
  props.searchResult?.id ? `highlight-${props.searchResult.id}` : null,
)

const highlightedSections = computed(() => {
  return props.sections.map((section) => {
    if (!props.searchResult || section.id.toLowerCase() !== props.searchResult.id.toLowerCase()) {
      return section
    }

    // The default richText policy drops <fn> and <list-item>, which would turn footnote and list paragraphs into numbered prose.
    const highlightedContent = handleHighlights(
      convertJatsTitleTagsToH3(section.bodytext || ''),
      props.searchResult.highlights,
      highlightScrollId.value || '',
      'jats',
    )
    return { ...section, bodytext: highlightedContent }
  })
})

const sectionLayouts = computed(() => buildSectionLayouts(highlightedSections.value, $parseStringToSharedDocument))

watch(() => props.searchResult, (searchResult) => {
  const targetId = highlightScrollId.value

  if (!searchResult?.id || !targetId) {
    return
  }

  nextTick(() => {
    const container = toValue(containers?.content)
    if (!container) {
      return
    }

    // The anchor exists only if `handleHighlights` found a match in the body text
    const highlightAnchor = document.getElementById(targetId)
    // `highlightedSections` matches section and result IDs without case sensitivity. The fallback should do the same.
    const sectionId = props.sections.find(section => section.id.toLowerCase() === searchResult.id.toLowerCase())?.id
    const targetElement = highlightAnchor ?? (sectionId ? document.getElementById(sectionId) : null)
    if (!targetElement) {
      return
    }

    scrollInstantWithCorrection(targetElement, container, {
      extraOffset: highlightAnchor ? Math.min(highlightAnchor.getBoundingClientRect().height, 100) : 0,
      onSettled: syncVisibleSectionFromScroll,
    })
  })
})

function attachClickEventsToFootnotes() {
  document.querySelectorAll('button[data-footnote-id]')
    .forEach((btn) => {
      btn.addEventListener('click', (event) => {
        const currentTarget = event.currentTarget as HTMLElement
        const isExpanded = currentTarget.getAttribute('aria-expanded') === 'true'
        currentTarget.setAttribute('aria-expanded', `${!isExpanded}`)
        const fnId = currentTarget.getAttribute('data-footnote-id')
        document.querySelector(`span[data-footnote-id="${fnId}"]`)
          ?.toggleAttribute('hidden')
      })
    })
}

function hashTargetId(): string | undefined {
  try {
    return decodeURIComponent(route.hash.slice(1)) || undefined
  }
  catch {
    return undefined
  }
}

function scrollToHashTarget() {
  const id = hashTargetId()
  if (props.searchResult?.id || !id) {
    return
  }

  nextTick(() => {
    const container = toValue(containers?.content)
    const target = document.getElementById(id)
    if (!container || !target) {
      return
    }

    // On a cold load the browser's fragment scroll moves the window too, and html would animate the reset
    window.scrollTo({ top: 0, behavior: 'instant' })
    scrollInstantWithCorrection(target, container, {
      onSettled: () => {
        syncVisibleSectionFromScroll()
        // Focus tells keyboard and screen-reader users where the deep link landed; the scroll alone is silent. A page marker is aria-hidden, so the block holding it takes the focus instead.
        const block = target.classList.contains('jats-page-break') ? target.closest('p[data-avsnitt], h1, h2, h3, h4, h5, h6, li, td, th, figcaption') ?? target.parentElement : target
        const focusTarget = (block ?? target) as HTMLElement
        focusTarget.tabIndex = -1
        focusTarget.focus({ preventScroll: true })
      },
    })
  })
}

// The router scrolls the window, never the reading container, so in-page hash changes need the same treatment as a cold load.
watch(() => route.hash, scrollToHashTarget)

onMounted(() => {
  if (props.footnotes === undefined) {
    attachClickEventsToFootnotes()
  }
  scrollToHashTarget()
})

const journalFootnotes = useJournalFootnotes(() => props.footnotes)
</script>

<template>
  <div>
    <div class="max-w-4xl mx-auto md:px-m">
      <div class="sticky top-0 max-md:top-[52px] z-10 md:px-m bg-surface-elevated">
        <slot name="header" />
      </div>

      <JournalsAbstract v-if="abstractData" class="md:px-m" :data="abstractData" />

      <template v-if="highlightedSections.length > 0">
        <div class="opacity-100">
          <div
            v-for="layout in sectionLayouts"
            :id="layout.id"
            :key="layout.id"
            class="md:px-m"
            data-citation-source="article"
            data-marking-content-type="journal-article"
            :data-marking-work-id="articleId"
            :data-marking-document-id="articleId"
            :data-marking-unit-id="layout.id"
            :data-marking-unit-title="layout.title"
            :data-citation-title="articleTitle || undefined"
            :data-citation-authors="articleAuthors?.join(', ')"
            :data-citation-journal-title="journal?.title ?? undefined"
            :data-citation-publisher="journal?.publisher?.[0] ?? undefined"
            :data-citation-year="journal?.year ?? undefined"
            :data-citation-volume="journal?.volume ?? undefined"
            :data-citation-issue="journal?.issue ?? undefined"
            :data-citation-first-page="articleFirstPage ?? undefined"
            :data-citation-last-page="articleLastPage ?? undefined"
            :data-citation-start-page="layout.pagination?.startPage"
            :data-citation-legal-areas="journal?.legal_areas?.join(', ') ?? undefined"
          >
            <ArticleSection
              :layout="layout"
              :footnotes="props.footnotes ? journalFootnotes.data.value : undefined"
              @open-footnote="journalFootnotes.open"
              @close-footnote="journalFootnotes.close"
            />
          </div>
        </div>
      </template>

      <p v-if="copyrightText" class="px-m mt-l text-secondary">
        {{ copyrightText }}
      </p>
    </div>
  </div>
</template>

<style scoped lang="postcss">
:deep(.body-text) {
  h1 {
    @apply font-body text-2xl sm:text-3xl font-semibold mb-m;
  }
  h1.chaptitle {
    @apply font-body text-2xl sm:text-3xl font-semibold mb-m;
  }
  h2 {
    @apply font-body text-xl sm:text-lg font-bold mb-m;
  }

  /* h3, h4 are handled by BodyText defaults */

  h1 span {
    @apply border-0 !important;
  }
  h1.chaptitle span.chap_no {
    @apply font-body text-2xl sm:text-3xl font-semibold;
  }
  h1.chaptitle span.chap_no small {
    @apply lowercase font-body text-2xl sm:text-3xl font-semibold;
  }
  h1.chaptitle span.chap_no1 {
    @apply font-body text-2xl sm:text-3xl font-semibold;
  }

  /* p is handled by BodyText defaults */

  p[data-avsnitt] {
    @apply relative scroll-mt-24;
  }
  /* No paragraph numbers in print show them instead on hover, focus or deep links. */
  p[data-avsnitt]::before {
    content: attr(data-avsnitt);
    content: attr(data-avsnitt) / "Avsnitt " attr(data-avsnitt);
    @apply hidden text-secondary text-sm tabular-nums mr-2xs;
  }
  p[data-avsnitt]:target::before,
  p[data-avsnitt]:focus-within::before {
    @apply inline;
  }
  p[data-avsnitt]:target {
    @apply bg-surface-hover;
  }
  @screen md {
    /* visibility, not opacity: a hidden number stays out of the accessibility tree, like the display: none below md. */
    p[data-avsnitt]::before {
      @apply block invisible absolute right-full top-0 mr-3xs w-8 text-right;
    }
    p[data-avsnitt]:target::before,
    p[data-avsnitt]:focus-within::before {
      @apply block visible;
    }
    @media (hover: hover) {
      p[data-avsnitt]:hover::before {
        @apply visible;
      }
    }
  }
  .jats-page-break {
    @apply scroll-mt-24;
  }
  .jats-page-break::before {
    content: "[s. " attr(data-page) "]";
    @apply text-secondary text-xs mx-3xs whitespace-nowrap font-normal not-italic;
  }
}
</style>
